// An append is safe only when every intervening revision created a known message.
// Edits, ACL changes, retention gaps and truncated event pages require a snapshot.
export function createdEventIds(events: { type?: string; revision?: string; object_id?: string }[], cursor: bigint, snapshot: bigint): Set<string> | null {
  if (!events.length || events.length > 100) return null;
  const ids = new Set<string>();
  let revision = cursor;
  for (const event of events) {
    if (event.type !== 'message.created' || !event.object_id || ids.has(event.object_id)
        || !event.revision || !/^\d+$/.test(event.revision) || BigInt(event.revision) !== ++revision) return null;
    ids.add(event.object_id);
  }
  return revision === snapshot ? ids : null;
}

type HistoryRow = { id: string; conversation_id: string; seq: string };
export function mergeCreatedMessages<T extends HistoryRow>(before: T[], added: T[], ids: Set<string>, conversationId: string): T[] | null {
  const last = before.at(-1);
  if (!last || !/^\d+$/.test(last.seq) || added.length !== ids.size) return null;
  const seen = new Set(before.map(row => row.id));
  let sequence = BigInt(last.seq);
  for (const row of added) {
    if (row.conversation_id !== conversationId || !ids.has(row.id) || seen.has(row.id)
        || !/^\d+$/.test(row.seq) || BigInt(row.seq) <= sequence) return null;
    sequence = BigInt(row.seq); seen.add(row.id);
  }
  return [...before, ...added].slice(-Math.max(100, before.length));
}

// Several edits/reactions may affect the same message in one contiguous event page.
export function changedEventIds(events: { type?: string; revision?: string; object_id?: string }[], cursor: bigint, snapshot: bigint): Set<string> | null {
  if (!events.length || events.length > 100) return null;
  const ids = new Set<string>(); let revision = cursor;
  for (const event of events) {
    if (!['message.updated','message.deleted','reaction.updated'].includes(event.type ?? '') || !event.object_id
        || !event.revision || !/^\d+$/.test(event.revision) || BigInt(event.revision) !== ++revision) return null;
    ids.add(event.object_id);
  }
  return revision === snapshot ? ids : null;
}

// Reader cursors do not modify message content. Only a complete contiguous page
// can advance the history cursor without reloading message rows; mixed events,
// gaps, truncation and access changes must keep the authoritative fallback.
export function onlyReadReceiptEvents(events:{type?:string;revision?:string}[],cursor:bigint,snapshot:bigint):boolean {
 if(!events.length||events.length>100)return false;
 let revision=cursor;
 for(const event of events) {
  if(event.type!=='receipt.updated'||!event.revision||!/^\d+$/.test(event.revision)||BigInt(event.revision)!==++revision)return false;
 }
 return revision===snapshot;
}
